import { useEffect, useState, useCallback } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  RadioButton,
  Checkbox,
  Button,
  Banner,
  Toast,
  Badge,
  Spinner,
} from "@shopify/polaris";
import { useVelocitySettings } from "../hooks/useVelocitySettings";

// Generate month options for the last 24 months
function getMonthOptions(): Array<{ label: string; value: string }> {
  const options: Array<{ label: string; value: string }> = [];
  const now = new Date();

  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "long",
    });
    options.push({ label, value });
  }

  return options;
}

export default function Settings() {
  const {
    settings,
    loading,
    error,
    loadSettings,
    saveSettings,
    startBackfill,
    pollBackfill,
  } = useVelocitySettings();

  const [method, setMethod] = useState<string>("rolling_90");
  const [selectedMonths, setSelectedMonths] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [backfillStatus, setBackfillStatus] = useState<string | null>(null);
  const [backfillRunning, setBackfillRunning] = useState(false);

  const monthOptions = getMonthOptions();

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (settings) {
      setMethod(settings.calculationMethod);
      setSelectedMonths(new Set(settings.selectedMonths));
    }
  }, [settings]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    const success = await saveSettings({
      calculationMethod: method,
      selectedMonths: Array.from(selectedMonths),
    });
    if (success) setToastMessage("Velocity settings saved");
    setSaving(false);
  }, [method, selectedMonths, saveSettings]);

  const handleMonthToggle = useCallback(
    (month: string) => {
      setSelectedMonths((prev) => {
        const next = new Set(prev);
        if (next.has(month)) {
          next.delete(month);
        } else {
          next.add(month);
        }
        return next;
      });
    },
    []
  );

  const handleBackfill = useCallback(async () => {
    setBackfillRunning(true);
    setBackfillStatus("Starting backfill...");

    const operationId = await startBackfill();
    if (!operationId) {
      setBackfillStatus("Failed to start backfill");
      setBackfillRunning(false);
      return;
    }

    setBackfillStatus("Backfill in progress...");

    // Poll until complete
    const poll = async () => {
      const result = await pollBackfill(operationId);

      if (result.status === "COMPLETED") {
        if (result.processed) {
          setBackfillStatus(
            `Backfill complete: ${result.variantsProcessed} variants, ${result.monthsPopulated} months`
          );
        } else {
          setBackfillStatus("Backfill complete, processing results...");
          // Poll once more to trigger processing
          setTimeout(poll, 3000);
          return;
        }
        setBackfillRunning(false);
      } else if (result.status === "FAILED") {
        setBackfillStatus("Backfill failed");
        setBackfillRunning(false);
      } else {
        // Still running
        setBackfillStatus(`Backfill in progress (${result.status})...`);
        setTimeout(poll, 5000);
      }
    };

    setTimeout(poll, 5000);
  }, [startBackfill, pollBackfill]);

  return (
    <>
      <Page title="Settings" backAction={{ content: "Vendors", url: "/" }}>
        {error && (
          <Layout.Section>
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout>
          {/* Velocity Settings */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  Sales Velocity Calculation
                </Text>
                <Text as="p" tone="subdued">
                  Controls how average weekly units are calculated for purchase
                  order suggestions. This matches the Access "Setup" behavior.
                </Text>

                {loading ? (
                  <Spinner size="small" />
                ) : (
                  <BlockStack gap="300">
                    <RadioButton
                      label="Rolling 90-Day Average"
                      helpText="Uses the last 3 months of sales data to calculate average weekly units"
                      checked={method === "rolling_90"}
                      id="rolling_90"
                      name="velocity_method"
                      onChange={() => setMethod("rolling_90")}
                    />
                    <RadioButton
                      label="Custom Month Selection"
                      helpText="Select specific months to include in the average (mirrors Access month checkboxes)"
                      checked={method === "custom_months"}
                      id="custom_months"
                      name="velocity_method"
                      onChange={() => setMethod("custom_months")}
                    />

                    {method === "custom_months" && (
                      <Card>
                        <BlockStack gap="200">
                          <Text variant="headingSm" as="h3">
                            Select Months to Include
                          </Text>
                          <InlineStack gap="300" wrap>
                            {monthOptions.map((opt) => (
                              <Checkbox
                                key={opt.value}
                                label={opt.label}
                                checked={selectedMonths.has(opt.value)}
                                onChange={() => handleMonthToggle(opt.value)}
                              />
                            ))}
                          </InlineStack>
                          {selectedMonths.size > 0 && (
                            <Text as="p" tone="subdued">
                              {selectedMonths.size} month(s) selected
                            </Text>
                          )}
                        </BlockStack>
                      </Card>
                    )}

                    <InlineStack gap="200">
                      <Button
                        variant="primary"
                        onClick={handleSave}
                        loading={saving}
                      >
                        Save Velocity Settings
                      </Button>
                    </InlineStack>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Historical Backfill */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  Historical Sales Backfill
                </Text>
                <Text as="p" tone="subdued">
                  Pull 12 months of order history from Shopify to populate the
                  sales velocity cache. Run this once after initial setup, or to
                  refresh all data.
                </Text>

                {backfillStatus && (
                  <Banner
                    tone={
                      backfillStatus.includes("complete")
                        ? "success"
                        : backfillStatus.includes("failed")
                          ? "critical"
                          : "info"
                    }
                  >
                    <p>{backfillStatus}</p>
                  </Banner>
                )}

                <Button onClick={handleBackfill} loading={backfillRunning}>
                  Run Historical Backfill
                </Button>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Email Configuration */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  Email Configuration
                </Text>
                <Text as="p" tone="subdued">
                  Email sending is configured via server environment variables.
                  Set these on the droplet to enable emailing POs to vendors:
                </Text>
                <Card>
                  <BlockStack gap="100">
                    <Text as="p" variant="bodyMd">
                      <strong>SMTP_HOST</strong> — SMTP server (e.g.,
                      smtp.gmail.com)
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>SMTP_PORT</strong> — Port (587 for TLS)
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>SMTP_USER</strong> — Email username
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>SMTP_PASS</strong> — Email password or app password
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>SMTP_FROM</strong> — From address (defaults to
                      SMTP_USER)
                    </Text>
                  </BlockStack>
                </Card>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* PO Numbering */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  PO Numbering
                </Text>
                <Text as="p" tone="subdued">
                  Format: PO-YYYY-NNNN (e.g., PO-2026-0001). Sequence is
                  auto-incrementing.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>

        {toastMessage && (
          <Toast
            content={toastMessage}
            onDismiss={() => setToastMessage(null)}
          />
        )}
      </Page>
    </>
  );
}
